import clsx from 'clsx';
import React from 'react';
import type { IconName, IconSize } from '../../icons/types';
import Icon from '../Icon/Icon';

export type ButtonVariant =
  | 'primary'
  | 'primary-outline'
  | 'primary-ghost'
  | 'primary-soft'
  | 'secondary'
  | 'secondary-outline'
  | 'secondary-ghost'
  | 'inverse-ghost'
  | 'neutral-soft'
  | 'neutral-outline'
  | 'dark'
  | 'warning'
  | 'info'
  | 'plain'
  | 'plain-primary';

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';
export type ButtonShape = 'rounded' | 'pill';
export type ButtonType = 'button' | 'submit' | 'reset';
export type ButtonIconSize = IconSize;

type ButtonProps = {
  ariaControls?: string;
  ariaExpanded?: boolean;
  ariaHasPopup?: 'dialog' | 'menu' | 'listbox' | 'tree' | 'grid';
  ariaLabel?: string;
  children?: React.ReactNode;
  className?: string;
  iconSize?: ButtonIconSize;
  id?: string;
  isDisabled?: boolean;
  isFullWidth?: boolean;
  isIconOnly?: boolean;
  isLoading?: boolean;
  leftIcon?: IconName;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  rightIcon?: IconName;
  shape?: ButtonShape;
  size?: ButtonSize;
  testid?: string;
  text?: string;
  type?: ButtonType;
  variant?: ButtonVariant;
};

function ButtonSpinner(): JSX.Element {
  return (
    <span aria-hidden="true" className="button__spinner">
      <svg className="button__spinner-svg" viewBox="0 0 24 24" focusable="false">
        <circle
          className="button__spinner-track"
          cx="12"
          cy="12"
          r="9"
          fill="none"
          strokeWidth="3"
        />
        <circle
          className="button__spinner-head"
          cx="12"
          cy="12"
          r="9"
          fill="none"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

function warnIfIconOnlyMissingAccessibleName(
  isIconOnly: boolean,
  text: string | undefined,
  ariaLabel: string | undefined,
  nativeAriaLabel: string | undefined,
  children: React.ReactNode,
): void {
  if (process.env.NODE_ENV === 'production' || !isIconOnly) return;
  const hasVisibleText = Boolean(text) || Boolean(children);
  const hasAccessibleName = Boolean(ariaLabel) || Boolean(nativeAriaLabel);
  if (!hasVisibleText && !hasAccessibleName) {
    // `console.error` (no `warn`) para que sea visible en dev tools y en el
    // output de tests (jest-axe también fallará por el mismo motivo). WCAG
    // 4.1.2: un botón icon-only sin texto necesita un nombre accesible.
    console.error('[Button] isIconOnly=true requires an `ariaLabel` (or visible text) to be accessible.');
  }
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      ariaControls,
      ariaExpanded,
      ariaHasPopup,
      ariaLabel,
      children,
      className,
      iconSize,
      isDisabled = false,
      isFullWidth = false,
      isIconOnly = false,
      isLoading = false,
      leftIcon,
      onClick,
      rightIcon,
      shape = 'rounded',
      size = 'md',
      testid,
      text,
      type = 'button',
      variant = 'primary',
      ...restProps
    },
    ref,
  ) => {
    const nativeAriaLabelFromRest = (restProps as { 'aria-label'?: string })['aria-label'];
    warnIfIconOnlyMissingAccessibleName(isIconOnly, text, ariaLabel, nativeAriaLabelFromRest, children);

    const isInteractionBlocked = isDisabled || isLoading;

    const rootClassName = clsx(
      'button',
      `button--variant-${variant}`,
      `button--size-${size}`,
      `button--shape-${shape}`,
      {
        'button--full-width': isFullWidth,
        'button--icon-only': isIconOnly,
        'button--loading': isLoading,
      },
      className,
    );

    // Override `--button-icon-size` via inline style so it wins over the
    // size + icon-only compound rules in `_button.scss` without needing
    // `!important`. Only applied when the consumer opts in via `iconSize`.
    const iconSizeStyle = iconSize
      ? ({ '--button-icon-size': `var(--ui-icon-size-${iconSize})` } as React.CSSProperties)
      : undefined;

    // Extract native aria-* out of restProps so they don't get overwritten
    // by the explicit aria-* attributes below. Call-sites may pass either
    // form (camelCase prop or native `aria-*`, e.g. spreading a trigger-props
    // object like `usePopover().getTriggerProps()`); the camelCase prop
    // takes precedence when both are provided.
    const {
      'aria-controls': nativeAriaControls,
      'aria-disabled': nativeAriaDisabled,
      'aria-expanded': nativeAriaExpanded,
      'aria-haspopup': nativeAriaHasPopup,
      'aria-label': nativeAriaLabel,
      ...restWithoutAria
    } = restProps as {
      'aria-controls'?: string;
      'aria-disabled'?: boolean | 'true' | 'false';
      'aria-expanded'?: React.AriaAttributes['aria-expanded'];
      'aria-haspopup'?: React.AriaAttributes['aria-haspopup'];
      'aria-label'?: string;
    } & Record<string, unknown>;
    const resolvedAriaControls = ariaControls ?? nativeAriaControls;
    const resolvedAriaExpanded = ariaExpanded ?? nativeAriaExpanded;
    const resolvedAriaHasPopup = ariaHasPopup ?? nativeAriaHasPopup;
    const resolvedAriaLabel = ariaLabel ?? nativeAriaLabel;
    const resolvedAriaDisabled = isDisabled || nativeAriaDisabled === true || nativeAriaDisabled === 'true' || undefined;

    return (
      <button
        {...restWithoutAria}
        ref={ref}
        aria-busy={isLoading || undefined}
        aria-controls={resolvedAriaControls}
        aria-disabled={resolvedAriaDisabled}
        aria-expanded={resolvedAriaExpanded}
        aria-haspopup={resolvedAriaHasPopup}
        aria-label={resolvedAriaLabel}
        className={rootClassName}
        data-testid={testid}
        disabled={isInteractionBlocked}
        onClick={onClick}
        style={iconSizeStyle}
        type={type}
      >
        <span className="button__content">
          {leftIcon && (
            <span aria-hidden="true" className="button__icon button__icon--left">
              <Icon color="inherit" name={leftIcon} size={iconSize} />
            </span>
          )}
          {!isIconOnly && text && <span className="button__text">{text}</span>}
          {!isIconOnly && children}
          {rightIcon && (
            <span aria-hidden="true" className="button__icon button__icon--right">
              <Icon color="inherit" name={rightIcon} size={iconSize} />
            </span>
          )}
        </span>
        {isLoading && <ButtonSpinner />}
      </button>
    );
  },
);

Button.displayName = 'Button';

export default Button;
